import Quill from 'quill';
import Delta from 'quill-delta';
import 'quill/dist/quill.snow.css';
import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import { useFeature } from '@treams/treams-hooks/useFeature';
import { useLocalize } from '@treams/treams-hooks/useLocalize';
import { ChevronLeftIcon } from '@treams/treams-icons';
import * as graphql from '@treams/treams-network';
import { Button } from '@treams/treams-ui/common/button/Button';
import { NumberedSubheading } from '@treams/treams-ui/common/NumberedSubheading';
import { Heading, Paragraph, Title } from '@treams/treams-ui/common/Text';
import { Toolbar } from '@treams/treams-ui/common/Toolbar';
import { QuillText } from '@treams/treams-ui/form/controls/QuillText';
import { tokens } from '@treams/treams-ui/theme/tokens';

import EvaluationSubmissionSection from '../EvaluationSubmissionSection';
import EvaluationSubmissionStatus from './EvaluationSubmissionStatus';
import Emoticon from './layout/Emoticon';
import EmoticonContainer from './layout/EmoticonContainer';
import EvaluationSubmissionSidebarLayout from './layout/EvaluationSubmissionSidebarLayout';
import LayoutSection from './layout/LayoutSection';

const EmployeeAndMomentTitle = styled.p`
	color: ${tokens.text.secondary};
	font-size: 14px;
	line-height: 24px;
	font-style: italic;
`;

const EditorContainer = styled.div`
	max-width: 650px;
	margin-bottom: 12px;

	.ql-container {
		font-family: inherit;
		font-size: 15px;
		border: 1px solid ${(props) => props.theme.colors.primary};
		border-radius: 6px;
		min-height: 100px;
	}
	.ql-editor {
		min-height: 100px;
	}
`;

const ItemContainer = styled(Flex)`
	&:not(:last-child) {
		margin-bottom: 48px;
	}
`;

const IGNORED_ITEM_TYPES = [
	graphql.EvaluationSectionItemType.FinalScore,
	graphql.EvaluationSectionItemType.Signature,
	graphql.EvaluationSectionItemType.DevelopmentPlan,
	graphql.EvaluationSectionItemType.SetGoals,
];

const VISUAL_ITEM_TYPES = [
	graphql.EvaluationSectionItemType.Title,
	graphql.EvaluationSectionItemType.Paragraph,
];

function ReadOnlyOpenQuestion({ value }: { value: unknown }) {
	return <QuillText value={value as Delta} />;
}

function ReadOnlySentiment({ value }: { value: number }) {
	const choices = ['😥', '😕', '🤔', '🙂', '😃'];
	return (
		<EmoticonContainer>
			{choices.map((emoji, index) => (
				<Emoticon selected={value === index} key={index}>
					{emoji}
				</Emoticon>
			))}
		</EmoticonContainer>
	);
}

function ReadOnlyStars({ value }: { value: number }) {
	return (
		<EmoticonContainer>
			{[1, 2, 3, 4, 5].map((_, index) => (
				<Emoticon key={index} selected={value >= index}>
					⭐
				</Emoticon>
			))}
		</EmoticonContainer>
	);
}

const CompetencyBullet = styled.div<{ filled?: boolean; party?: 'employee' | 'manager' | 'both' }>`
	width: 32px;
	height: 32px;
	border-radius: 16px;
	background: ${(props) => {
		if (!props.filled) return '#dae0e8';
		if (props.party === 'employee') return props.theme.colors.employee;
		if (props.party === 'manager') return props.theme.colors.manager;
		if (props.party === 'both') {
			return `linear-gradient(90deg, ${props.theme.colors.employee} 50%, ${props.theme.colors.manager} 50%)`;
		}
		return '#dae0e8';
	}};
	${(props) => !props.filled && `transform: scale(0.25, 0.25);`}
`;

const CompetencyLabel = styled.span`
	color: ${tokens.text.secondary};
	font-size: 13px;
`;

type FormItemType =
	graphql.EvaluationSubmissionFragment['evaluationForm']['sections'][0]['items'][0];
type SubmissionItemType = graphql.EvaluationSubmissionFragment['viewer']['sections'][0]['items'][0];
type CompetencyLevelType = FormItemType['competency'];

function ReadOnlyCompetency({
	levels,
	employeeScore,
	managerScore,
}: {
	levels: NonNullable<CompetencyLevelType>['competency']['levels'];
	employeeScore?: number | null;
	managerScore?: number | null;
}) {
	const { localize } = useLocalize();

	const enabledLevels = useMemo(() => levels.filter((level) => level.enabled), [levels]);

	const bullets = useMemo(
		() =>
			levels.map((level, i) => {
				if (!level.enabled) return null;
				const isEmployee = employeeScore === i;
				const isManager = managerScore === i;
				const party =
					isEmployee && isManager
						? 'both'
						: isEmployee
							? 'employee'
							: isManager
								? 'manager'
								: undefined;
				return <CompetencyBullet key={i} filled={!!party} party={party} />;
			}),
		[levels, employeeScore, managerScore],
	);

	return (
		<Flex alignItems="center" style={{ gap: 8 }}>
			{enabledLevels.length > 0 && (
				<CompetencyLabel>{localize(enabledLevels[0].level.title).text}</CompetencyLabel>
			)}
			{bullets}
			{enabledLevels.length > 0 && (
				<CompetencyLabel>
					{localize(enabledLevels[enabledLevels.length - 1].level.title).text}
				</CompetencyLabel>
			)}
		</Flex>
	);
}

function ReadOnlyItem({
	formItem,
	viewerItem,
	employeeItem,
	managerItem,
	questionNumber,
}: {
	formItem: FormItemType;
	viewerItem: NonNullable<SubmissionItemType>;
	employeeItem?: SubmissionItemType;
	managerItem?: SubmissionItemType;
	questionNumber: number;
}) {
	const { localize } = useLocalize();
	const multipleChoiceEnabled = useFeature(graphql.FeatureName.ConversationMultipleChoice);

	const title = useMemo(() => {
		if (multipleChoiceEnabled && formItem.multipleChoice) {
			return localize(formItem.multipleChoice.title).text;
		}
		if (formItem.openQuestion) return localize(formItem.openQuestion.title).text;
		if (formItem.stars) return localize(formItem.stars.title).text;
		if (formItem.sentiment) return localize(formItem.sentiment.title).text;
		if (formItem.competency) return localize(formItem.competency.competency.title).text;
		if (formItem.reviewGoals) return 'Goal Review';
		return null;
	}, [formItem, localize, multipleChoiceEnabled]);

	const answer = useMemo(() => {
		switch (viewerItem.type) {
			case graphql.EvaluationSectionItemType.OpenQuestion:
				return viewerItem.openQuestion ? (
					<ReadOnlyOpenQuestion value={viewerItem.openQuestion} />
				) : null;

			case graphql.EvaluationSectionItemType.Sentiment:
				return viewerItem.sentiment !== null && viewerItem.sentiment !== undefined ? (
					<ReadOnlySentiment value={viewerItem.sentiment} />
				) : null;

			case graphql.EvaluationSectionItemType.Stars:
				return viewerItem.stars !== null && viewerItem.stars !== undefined ? (
					<ReadOnlyStars value={viewerItem.stars} />
				) : null;

			case graphql.EvaluationSectionItemType.Competency:
				return formItem.competency ? (
					<ReadOnlyCompetency
						levels={formItem.competency.competency.levels}
						employeeScore={employeeItem?.competency ?? undefined}
						managerScore={managerItem?.competency ?? undefined}
					/>
				) : null;

			case graphql.EvaluationSectionItemType.MultipleChoice:
				if (!multipleChoiceEnabled || !formItem.multipleChoice) {
					return null;
				}

				return (viewerItem.multipleChoice ?? [])
					.filter((index): index is number => index != null)
					.sort((left, right) => left - right)
					.map((index) => {
						const text = formItem.multipleChoice?.options[index]?.text;
						return {
							index,
							label: text ? localize(text).text : '',
						};
					})
					.filter((option) => option.label)
					.map((option) => <Paragraph key={option.index}>{option.label}</Paragraph>);

			default:
				return null;
		}
	}, [
		viewerItem,
		formItem.competency,
		formItem.multipleChoice,
		employeeItem?.competency,
		managerItem?.competency,
		multipleChoiceEnabled,
		localize,
	]);

	const competencyDescription = useMemo(
		() =>
			formItem.competency ? localize(formItem.competency.competency.description ?? [])?.text : null,
		[formItem.competency, localize],
	);

	return (
		<ItemContainer flexDirection="column">
			{title && <NumberedSubheading index={questionNumber}>{title}</NumberedSubheading>}
			{competencyDescription && (
				<Box mb={3}>
					<QuillText value={competencyDescription} />
				</Box>
			)}
			<Box>{answer}</Box>
		</ItemContainer>
	);
}

export default function EvaluationSubmissionFollowup() {
	const { t } = useTranslation();
	const { localize } = useLocalize();
	const multipleChoiceEnabled = useFeature(graphql.FeatureName.ConversationMultipleChoice);
	const { conversationID } = useParams<{ conversationID: string }>();
	const editorRef = useRef<HTMLDivElement>(null);
	const quillRef = useRef<Quill | null>(null);

	const { data } = graphql.useEvaluationSubmissionQuery({
		variables: {
			id: conversationID,
		},
		skip: !conversationID,
	});

	const submission = useMemo(() => data?.evaluationSubmission, [data]);

	useLayoutEffect(() => {
		if (!editorRef.current || quillRef.current) return;

		quillRef.current = new Quill(editorRef.current, {
			theme: 'snow',
			modules: { toolbar: false },
			placeholder: '...',
		});
	}, [submission]);

	const countsTowardQuestionNumber = useCallback(
		(type: graphql.EvaluationSectionItemType) => {
			if (IGNORED_ITEM_TYPES.includes(type) || VISUAL_ITEM_TYPES.includes(type)) {
				return false;
			}

			return multipleChoiceEnabled || type !== graphql.EvaluationSectionItemType.MultipleChoice;
		},
		[multipleChoiceEnabled],
	);

	// Calculate question numbers (excluding visual elements)
	const getQuestionNumber = useCallback(
		(sectionIndex: number, itemIndex: number) => {
			if (!submission) return 0;
			let count = 0;
			for (let s = 0; s <= sectionIndex; s++) {
				const items = submission.evaluationForm.sections[s].items;
				const maxItem = s === sectionIndex ? itemIndex : items.length;
				for (let i = 0; i < maxItem; i++) {
					if (countsTowardQuestionNumber(items[i].type)) {
						count++;
					}
				}
			}
			return count + 1;
		},
		[submission, countsTowardQuestionNumber],
	);

	// Filter sections to only include those with visible items
	const filteredSections = useMemo(
		() =>
			submission?.evaluationForm.sections
				.map((section, index) => ({ section, index }))
				.filter(({ section }) =>
					section.items.some((item) => countsTowardQuestionNumber(item.type)),
				) ?? [],
		[submission, countsTowardQuestionNumber],
	);

	const noopSetSignChecked = useCallback(() => {}, []);
	const noopSetShowSignModal = useCallback(() => {}, []);

	const [isSidebarOpen, setIsSidebarOpen] = useState(false);

	if (!submission) return null;

	return (
		<EvaluationSubmissionSidebarLayout
			id={conversationID}
			user={submission.employee.user as graphql.User}
			onSidebarOpen={(newValue) => setIsSidebarOpen(newValue)}
			isSideBarOpen={isSidebarOpen}
		>
			<Box pt={4}>
				<LayoutSection>
					<Toolbar mb={30}>
						<Button
							icon={<ChevronLeftIcon />}
							link="/conversations"
							title={t('goals.show.toolbar.back')}
							type="secondary"
						/>
					</Toolbar>
				</LayoutSection>
			</Box>
			<LayoutSection>
				<Box mb={4}>
					<Title mb={2}>{localize(submission.evaluationForm.title).text}</Title>
					{submission.moment && (
						<EmployeeAndMomentTitle>
							{t('evaluations.submission.about.employee', {
								employee: `${submission.employee.user.firstName + ' '}
							${submission.employee.user.lastName}`,
							})}
							{submission.moment &&
								'. ' +
									`${t('evaluations.submission.momentTitle', {
										title: localize(submission.moment.momentTemplate.title).text,
									})}`}
						</EmployeeAndMomentTitle>
					)}

					{submission.evaluationForm.description && (
						<QuillText value={localize(submission.evaluationForm.description).text} />
					)}
				</Box>
				<EvaluationSubmissionStatus
					submission={submission}
					updateLoading={false}
					flashSignSection={false}
					signChecked={false}
					setSignChecked={noopSetSignChecked}
					setShowSignModal={noopSetShowSignModal}
				/>
				<Box p={3}>
					<Heading mb={2}>Gespreksinhoud</Heading>
					<EditorContainer>
						<div ref={editorRef} />
					</EditorContainer>
				</Box>

				<Box p={3}>
					<Heading mb={2}>Voorbereiding</Heading>
				</Box>
			</LayoutSection>

			{filteredSections.map(({ section, index: sectionIndex }) => (
				<LayoutSection key={sectionIndex}>
					<EvaluationSubmissionSection mb={4}>
						{section.items.map((formItem, itemIndex) => {
							if (!countsTowardQuestionNumber(formItem.type)) {
								return null;
							}

							const viewerItem = submission.viewer.sections[sectionIndex]?.items[itemIndex];
							const employeeItem = submission.employee.sections[sectionIndex]?.items[itemIndex];
							const managerItem = submission.manager.sections[sectionIndex]?.items[itemIndex];

							if (!viewerItem) return null;

							return (
								<ReadOnlyItem
									key={itemIndex}
									formItem={formItem}
									viewerItem={viewerItem}
									employeeItem={employeeItem}
									managerItem={managerItem}
									questionNumber={getQuestionNumber(sectionIndex, itemIndex)}
								/>
							);
						})}
					</EvaluationSubmissionSection>
				</LayoutSection>
			))}
		</EvaluationSubmissionSidebarLayout>
	);
}
